/**
 * certs-app-view.js — pure core for the hostname-first Certificates tab on
 * `/my/apps/:id`.
 *
 * The tab used to list certificate *objects* from two per-environment
 * endpoints, so an app whose real addresses are `cslogs-kibana.caucasus.systems`
 * and `cslogs-elasticsearch.caucasus.systems` showed an uploaded certificate as
 * "Unknown" with empty cells, and "Request certificate" opened on an empty
 * Domain field. This module turns the app's OWN hostnames into the subject of
 * the tab and pre-fills the request from the group the platform already knows.
 *
 * Backend contract (cluster-api):
 *   GET /api/cluster/certificates/app-view
 *       ?cluster=&namespace=&workload=&template_id=
 *   data = {
 *     cluster, namespace, workload,
 *     hostnames: [{ hostname, source: 'ingress'|'service-dns',
 *                   state: 'covered'|'expiring'|'issued_unbound'|'uncovered'
 *                          |'upload_only'|'unknown',
 *                   covering: {registry_id, subject, not_after, days_left,
 *                              mode, matched_by, secret} | null,
 *                   domain: {domain, zone, certificate_policy} | null,
 *                   served: {ingress, secret, bound} | null,
 *                   public_dns: 'resolves'|'absent'|'unknown',
 *                   public_ip: string | null,
 *                   recommended_private: bool,
 *                   reason: null | string }],
 *     group: { issuable: [], upload_only: [], unbound: [], issued_unbound: [],
 *              suggested_certificate: {common_name, dns_names: []} | null },
 *     registration: {id, state, secret_name} | null,
 *     errors: []
 *   }
 *
 * The endpoint may not be deployed yet: `createAppViewApi().load()` never
 * throws and reports `missing: true` for a 404 so the tab falls back to the
 * certificate list it has always shown.
 *
 * Honesty rule encoded here: a hostname the platform could not resolve is
 * `unknown` WITH its reason — never "not covered". `uncovered` means the
 * platform looked and found nothing.
 *
 * Second honesty rule (2026-09-24, `cslogs`): "issued" is not "served". A
 * certificate can exist, be valid, cover the name — and the browser still get
 * an error, because the Ingress names a DIFFERENT Secret. That is
 * `issued_unbound`, and the fix is a BIND, not another order; which is why it
 * is deliberately absent from `group.issuable`. A name with no Ingress at all
 * (`served: null`, or `served.ingress: null` — a LoadBalancer `service-dns`
 * address) is not a broken bind and must never be shown as one.
 *
 * Only relative imports — keep this module pure so `node --test` runs it.
 */

import { formatExpiry } from './certs-shape.js'

export const APP_VIEW_PATH = '/cluster/certificates/app-view'

function str(v) {
  return String(v === null || v === undefined ? '' : v).trim()
}

function lower(v) {
  return str(v).toLowerCase()
}

function nameList(list) {
  if (!Array.isArray(list)) return []
  const out = []
  const seen = new Set()
  list.forEach((entry) => {
    const name = lower(entry && typeof entry === 'object' ? (entry.hostname || entry.name) : entry)
    if (!name || seen.has(name)) return
    seen.add(name)
    out.push(name)
  })
  return out
}

// ── state vocabulary ────────────────────────────────────────────────────────
// Deliberately the same five tones the tab already uses for certificate rows,
// so one glance reads across both sections.

export const HOSTNAME_STATES = [
  'covered', 'expiring', 'issued_unbound', 'uncovered', 'upload_only', 'unknown'
]

const STATE_BADGES = {
  covered: { label: 'Covered', tone: 'success', icon: 'fa-circle-check' },
  expiring: { label: 'Expiring soon', tone: 'warning', icon: 'fa-clock' },
  // Not another clock: the certificate is fine, the wiring is not. Its own
  // tone so a column of pills never reads `issued_unbound` as `expiring`.
  issued_unbound: { label: 'Issued, not served', tone: 'served', icon: 'fa-link-slash' },
  uncovered: { label: 'Not covered', tone: 'danger', icon: 'fa-shield-halved' },
  upload_only: { label: 'Upload only', tone: 'info', icon: 'fa-stamp' },
  unknown: { label: 'Unknown', tone: 'neutral', icon: 'fa-circle-question' }
}

/** Badge descriptor for a hostname state; anything unrecognised is `unknown`. */
export function hostnameStateBadge(state) {
  return STATE_BADGES[lower(state)] || STATE_BADGES.unknown
}

const SOURCE_LABELS = {
  ingress: 'Ingress route',
  'service-dns': 'Service address',
  service_dns: 'Service address'
}

/** Where the platform found this hostname, in the words the portal uses elsewhere. */
export function sourceLabel(source) {
  const key = lower(source)
  return SOURCE_LABELS[key] || (key ? str(source) : 'Unknown source')
}

/** `acme` → auto-issued, `byo`/`upload` → uploaded. Never guesses beyond that. */
export function originLabel(mode) {
  const m = lower(mode)
  if (m === 'acme') return 'Auto-issued'
  if (m === 'byo' || m === 'upload' || m === 'uploaded') return 'Uploaded by you'
  return m ? str(mode) : null
}

export const NO_REASON_NOTE = 'The platform did not report a reason.'
export const UPLOAD_ONLY_HINT = 'Your organisation supplies this certificate — upload it here.'
export const NO_DOMAIN_HINT = 'No domain is registered for this hostname yet. Add it in Domains & SSL first.'

// ── public DNS advice (drives the Private / Public toggle, A-20) ────────────
// The backend reports, per hostname, whether the name answers on public DNS.
// A name that does NOT is a private name: issuing it over a plain HTTP-01
// challenge can never succeed, which is exactly the defect this replaces. An
// older backend sends nothing — then the answer is `unknown`, and unknown
// means private, because private is the safe lane (the platform publishes a
// temporary public record for the challenge and removes it afterwards).

export const PUBLIC_DNS_STATES = ['resolves', 'absent', 'unknown']

const PUBLIC_DNS_COPY = {
  resolves: 'Answers on public DNS',
  absent: 'No public DNS record',
  unknown: 'Public DNS not checked'
}

/** The A-20 sentence shown once beside the Private / Public toggles. */
export const A20_NOTE =
  'A public record is published only while the certificate is issued, then removed.'

/** `resolves` | `absent` | `unknown` for one raw hostname entry. */
export function publicDnsOf(entry) {
  const raw = lower(entry && (entry.public_dns || entry.publicDns))
  return PUBLIC_DNS_STATES.includes(raw) ? raw : 'unknown'
}

/** One line naming what the platform knows about this name's public DNS. */
export function publicDnsLabel(state, ip) {
  const key = PUBLIC_DNS_STATES.includes(lower(state)) ? lower(state) : 'unknown'
  const addr = str(ip)
  return key === 'resolves' && addr ? `${PUBLIC_DNS_COPY.resolves} · ${addr}` : PUBLIC_DNS_COPY[key]
}

/**
 * Should this name be requested as private? The backend's explicit
 * `recommended_private` wins; otherwise anything that does not resolve
 * publicly — including `unknown` — is private.
 */
export function recommendedPrivateOf(entry) {
  const raw = entry && typeof entry === 'object'
    ? (entry.recommended_private !== undefined ? entry.recommended_private : entry.recommendedPrivate)
    : undefined
  if (typeof raw === 'boolean') return raw
  return publicDnsOf(entry) !== 'resolves'
}

// ── normalisation ───────────────────────────────────────────────────────────

/** Normalise one `covering` block; returns null when there is nothing to show. */
export function normalizeCovering(covering) {
  if (!covering || typeof covering !== 'object') return null
  const daysLeft = typeof covering.days_left === 'number' ? covering.days_left : null
  return {
    registryId: covering.registry_id || covering.registryId || null,
    subject: str(covering.subject) || null,
    notAfter: covering.not_after || covering.notAfter || null,
    daysLeft,
    mode: str(covering.mode) || null,
    matchedBy: str(covering.matched_by || covering.matchedBy) || null,
    secret: str(covering.secret) || null,
    // Not in the documented shape, but rendered verbatim when a backend sends it.
    issuer: str(covering.issuer || covering.issuer_name) || null
  }
}

// ── where a name is actually served (ADDITIVE, 2026-09-24) ──────────────────

export const NO_INGRESS_NOTE = 'Load-balancer address, no ingress'

/**
 * Normalise one `served` block into `{ ingress, secret, bound, hasIngress }`,
 * or null when the backend sent nothing at all.
 *
 * Two shapes mean the same thing and BOTH mean "there is no Ingress here":
 * `served: null`, and `served: {ingress: null, secret: null, bound: false}` —
 * which is what a LoadBalancer `service-dns` name gets. Neither is a broken
 * bind, so `hasIngress` is the flag every caller reads, never truthiness of
 * the block itself.
 */
export function normalizeServed(served) {
  if (!served || typeof served !== 'object') return null
  const ingress = lower(served.ingress) || null
  const secret = str(served.secret) || null
  return { ingress, secret, bound: served.bound === true, hasIngress: !!ingress }
}

/**
 * Where this name is served, in one line — or null when there is nothing
 * honest to say.
 *
 *   an Ingress serves it   → "via kibana-ingress · secret cslogs-tls"
 *   a service-dns name     → "Load-balancer address, no ingress"
 *   the backend said nothing about it → null (never "not served")
 */
export function servedLine(row) {
  if (!row) return null
  const s = row.served
  // No Ingress — whether the backend said `served: null` or sent the empty
  // block. Only a name the platform found OUTSIDE an Ingress can be described
  // as a load-balancer address; for anything else silence beats a guess, and
  // neither is ever "not served".
  if (!s || !s.hasIngress) {
    const src = lower(row.source)
    return src === 'service-dns' || src === 'service_dns' ? NO_INGRESS_NOTE : null
  }
  return s.secret ? `via ${s.ingress} · secret ${s.secret}` : `via ${s.ingress}`
}

/** Normalise one `hostnames[]` entry into the row the table renders. */
export function normalizeHostname(entry) {
  const hostname = lower(entry && (entry.hostname || entry.name))
  const rawState = lower(entry && entry.state)
  const state = HOSTNAME_STATES.includes(rawState) ? rawState : 'unknown'
  const domain = entry && entry.domain && typeof entry.domain === 'object'
    ? {
      domain: lower(entry.domain.domain),
      zone: str(entry.domain.zone) || null,
      certificatePolicy: lower(entry.domain.certificate_policy || entry.domain.certificatePolicy) || null
    }
    : null
  const covering = normalizeCovering(entry && entry.covering)
  const served = normalizeServed(entry && entry.served)
  // The same reading cluster-api does: an Ingress serves this name, and the
  // Secret it names is NOT the one holding the covering certificate. The
  // backend refines `covered` into `issued_unbound` on exactly this — but it
  // deliberately leaves `expiring` alone (renewal is the more urgent action),
  // so a row can be expiring AND unbound. Both are genuinely outstanding, so
  // the bind action is driven by the mismatch, not by the state alone.
  const servedMismatch = !!(served && served.hasIngress && served.bound === false &&
    covering && covering.secret && served.secret && served.secret !== covering.secret)
  const reason = str(entry && entry.reason) || null
  const uploadOnly = state === 'upload_only' || (domain && domain.certificatePolicy === 'byo-only')
  const publicDns = publicDnsOf(entry)
  const publicIp = str(entry && (entry.public_ip || entry.publicIp)) || null
  const row = {
    hostname,
    source: lower(entry && entry.source) || null,
    sourceLabel: sourceLabel(entry && entry.source),
    state,
    badge: hostnameStateBadge(state),
    covering,
    served,
    domain,
    reason,
    uploadOnly,
    publicDns,
    publicIp,
    publicDnsLabel: publicDnsLabel(publicDns, publicIp),
    recommendedPrivate: recommendedPrivateOf(entry),
    // What the customer may do about THIS name. `issued_unbound` is NOT
    // generable: a second certificate does not change which Secret the
    // Ingress names. Its action is `canBind`.
    canGenerate: !uploadOnly && !!domain && state !== 'unknown' && state !== 'issued_unbound',
    canUpload: !!domain,
    canBind: state === 'issued_unbound' || servedMismatch
  }
  row.servedLine = servedLine(row)
  return row
}

/**
 * Normalise the whole `data` payload. Tolerates a missing `group` by deriving
 * one from the hostname rows, so an older/partial backend still renders.
 */
export function normalizeAppView(data) {
  const d = data && typeof data === 'object' ? data : {}
  const hostnames = (Array.isArray(d.hostnames) ? d.hostnames : [])
    .map(normalizeHostname)
    .filter((r) => r.hostname)

  const g = d.group && typeof d.group === 'object' ? d.group : {}
  const derivedIssuable = hostnames.filter((r) => r.canGenerate).map((r) => r.hostname)
  const derivedUploadOnly = hostnames.filter((r) => r.uploadOnly).map((r) => r.hostname)
  const derivedUnbound = hostnames.filter((r) => !r.domain).map((r) => r.hostname)
  const derivedIssuedUnbound = hostnames.filter((r) => r.canBind).map((r) => r.hostname)

  const issuable = Array.isArray(g.issuable) ? nameList(g.issuable) : derivedIssuable
  const uploadOnly = Array.isArray(g.upload_only || g.uploadOnly)
    ? nameList(g.upload_only || g.uploadOnly)
    : derivedUploadOnly
  const unbound = Array.isArray(g.unbound) ? nameList(g.unbound) : derivedUnbound
  // Deliberately never merged into `issuable`: ordering another certificate
  // does not fix a bind, and offering "Request certificate" for a name that
  // already has one is how `cslogs` burned a production order.
  const issuedUnbound = Array.isArray(g.issued_unbound || g.issuedUnbound)
    ? nameList(g.issued_unbound || g.issuedUnbound)
    : derivedIssuedUnbound

  const sc = g.suggested_certificate || g.suggestedCertificate || null
  const suggested = sc && typeof sc === 'object'
    ? { commonName: lower(sc.common_name || sc.commonName), dnsNames: nameList(sc.dns_names || sc.dnsNames) }
    : null

  const reg = d.registration && typeof d.registration === 'object' ? d.registration : null

  return {
    cluster: str(d.cluster) || null,
    namespace: str(d.namespace) || null,
    workload: str(d.workload) || null,
    hostnames,
    group: { issuable, uploadOnly, unbound, issuedUnbound, suggested },
    registration: reg
      ? { id: reg.id || null, state: str(reg.state) || null, secretName: str(reg.secret_name || reg.secretName) || null }
      : null,
    errors: (Array.isArray(d.errors) ? d.errors : [])
      .map((e) => (typeof e === 'string' ? e : str(e && (e.message || e.error)) || JSON.stringify(e)))
      .filter(Boolean)
  }
}

// ── rendering helpers ───────────────────────────────────────────────────────

/**
 * The certificate cell for one hostname row: the facts that exist, never an
 * empty cell. An `unknown` row renders its reason instead of a blank.
 */
export function certificateLines(row) {
  if (!row) return []
  if (row.state === 'unknown') return [row.reason || NO_REASON_NOTE]
  const c = row.covering
  // A certificate that exists but is not the one the Ingress names: say which
  // certificate was issued AND which Secret the edge actually presents.
  if (row.state === 'issued_unbound' && c) {
    const lines = []
    const origin = originLabel(c.mode)
    const head = [c.issuer, origin].filter(Boolean).join(' · ')
    if (head) lines.push(head)
    if (c.subject && c.subject !== row.hostname) lines.push(c.subject)
    if (c.secret) lines.push(`issued into ${c.secret}`)
    return lines.length ? lines : ['A certificate exists, but the ingress names another secret.']
  }
  if (!c) {
    if (row.uploadOnly) return [UPLOAD_ONLY_HINT]
    if (!row.domain) return [NO_DOMAIN_HINT]
    return ['No certificate covers this hostname yet.']
  }
  const lines = []
  const origin = originLabel(c.mode)
  const head = [c.issuer, origin].filter(Boolean).join(' · ')
  if (head) lines.push(head)
  if (c.subject && c.subject !== row.hostname) lines.push(c.subject)
  if (c.matchedBy && c.matchedBy !== 'exact') lines.push(`matched by ${c.matchedBy}`)
  if (c.secret) lines.push(c.secret)
  return lines.length ? lines : ['Covered by a certificate the platform delivered.']
}

/** `{ text, relative }` for the Expires cell — `—` only when there is genuinely no date. */
export function expiryCell(row) {
  const c = row && row.covering
  if (!c || !c.notAfter) return { text: '—', relative: null }
  return formatExpiry(c.notAfter, c.daysLeft)
}

/** The one sentence explaining what this hostname needs, or null when it is fine. */
export const ISSUED_UNBOUND_FALLBACK_NOTE =
  'A certificate covers this hostname, but the ingress still names a different secret.'

export function hostnameNote(row) {
  if (!row) return null
  if (row.state === 'unknown') return row.reason || NO_REASON_NOTE
  // Before the upload-only / no-domain branches: this name HAS a certificate,
  // so neither "upload one" nor "claim the domain" is the thing to do.
  if (row.state === 'issued_unbound') return row.reason || ISSUED_UNBOUND_FALLBACK_NOTE
  if (row.uploadOnly) return UPLOAD_ONLY_HINT
  if (!row.domain) return NO_DOMAIN_HINT
  if (row.state === 'expiring') {
    // Expiring AND unbound: the backend's own reason carries both facts
    // ("expires in 9 days; certificate issued but the ingress still uses …").
    // The day count alone would hide the half the customer can act on now.
    if (row.canBind && row.reason) return row.reason
    const d = row.covering && typeof row.covering.daysLeft === 'number' ? row.covering.daysLeft : null
    return d === null ? 'This certificate is close to expiry.' : `This certificate expires in ${d} day${d === 1 ? '' : 's'}.`
  }
  if (row.state === 'uncovered') return 'Request a certificate to serve this hostname over HTTPS.'
  return null
}

/** Counts + the one-line summary above the table. */
export function groupSummary(view) {
  const rows = (view && view.hostnames) || []
  const count = (state) => rows.filter((r) => r.state === state).length
  const covered = count('covered')
  const expiring = count('expiring')
  // Counted on its own, never folded into `covered`: the summary is the only
  // place a customer sees that something is issued and still not serving.
  const issuedUnbound = count('issued_unbound')
  const uncovered = count('uncovered')
  const uploadOnly = count('upload_only')
  const unknown = count('unknown')
  const total = rows.length
  const parts = []
  if (covered) parts.push(`${covered} covered`)
  if (expiring) parts.push(`${expiring} expiring soon`)
  if (issuedUnbound) parts.push(`${issuedUnbound} issued, not served`)
  if (uncovered) parts.push(`${uncovered} not covered`)
  if (uploadOnly) parts.push(`${uploadOnly} upload only`)
  if (unknown) parts.push(`${unknown} unknown`)
  const line = total
    ? `${total} hostname${total === 1 ? '' : 's'} · ${parts.join(' · ')}`
    : 'No hostnames found for this app yet.'
  return { total, covered, expiring, issuedUnbound, uncovered, uploadOnly, unknown, line }
}

// ── the request group ───────────────────────────────────────────────────────

/**
 * What the request dialogue opens with: `group.suggested_certificate` when the
 * backend sent one, else the issuable names. Upload-only names are stripped —
 * Generate is never offered for a `byo-only` domain, whatever the server sent.
 */
export function suggestedRequest(view) {
  if (!view) return null
  const group = view.group || {}
  const banned = new Set(group.uploadOnly || [])
  const issuable = (group.issuable || []).filter((n) => !banned.has(n))
  const s = group.suggested
  let names = []
  if (s && (s.commonName || (s.dnsNames || []).length)) {
    names = nameList([s.commonName, ...(s.dnsNames || [])]).filter((n) => !banned.has(n))
  }
  if (!names.length) names = issuable
  if (!names.length) return null
  return { commonName: names[0], additional: names.slice(1), all: names }
}

/** Is "Request certificate" available, and if not, the plain reason why. */
export function requestAvailability(view) {
  // No app-view (404 fallback / older backend): behave as the tab always did.
  if (!view) return { enabled: true, reason: null }
  const suggestion = suggestedRequest(view)
  if (suggestion) return { enabled: true, reason: null }
  const group = view.group || {}
  if ((group.uploadOnly || []).length) {
    return {
      enabled: false,
      reason: 'Your organisation supplies the certificates for these hostnames. Upload one instead.'
    }
  }
  if ((group.unbound || []).length) {
    return {
      enabled: false,
      reason: 'None of this app’s hostnames has a registered domain yet. Add the domain in Domains & SSL first.'
    }
  }
  if (!(view.hostnames || []).length) {
    return { enabled: false, reason: 'This app doesn’t publish a hostname yet, so there is nothing to secure.' }
  }
  if ((group.issuedUnbound || []).length) {
    return {
      enabled: false,
      reason: 'These hostnames already have a certificate — the ingress just doesn’t use it yet. Bind it instead of ordering another.'
    }
  }
  return { enabled: false, reason: 'The platform can’t issue for this app’s hostnames right now.' }
}

// ── binding an ingress to the certificate it should serve ───────────────────
// `POST /cluster/certificates/registrations/<id>/bind-ingresses`. Delivery
// runs this by itself; the manual action exists for registrations delivered
// BEFORE the bind shipped, and to re-assert it after someone edits an Ingress
// by hand. Gated like every other registration write: `certificate.issue`.

export const NO_REGISTRATION_TO_BIND =
  'The platform has no certificate registration for this app yet, so there is nothing to bind.'

/** `{ enabled, registrationId, reason }` — may this view be re-bound, and if not, why not. */
export function bindAvailability(view) {
  const regId = (view && view.registration && view.registration.id) || null
  if (!regId) return { enabled: false, registrationId: null, reason: NO_REGISTRATION_TO_BIND }
  return { enabled: true, registrationId: regId, reason: null }
}

const SKIP_COPY = {
  already_bound: 'already names this certificate’s secret — nothing to change',
  no_intersection: 'serves none of this certificate’s hostnames',
  would_narrow_coverage: 'left as it is — this certificate doesn’t cover every hostname it serves',
  owner_certificate: 'left as it is — your organisation supplies the certificate here'
}

/** A skip reason in the portal's words; an unrecognised one is shown verbatim. */
export function skipReasonLabel(reason, skip = null) {
  const key = lower(reason)
  const copy = SKIP_COPY[key]
  if (!copy) return key ? str(reason) : 'left unchanged'
  if (key === 'would_narrow_coverage') {
    const missing = nameList((skip && (skip.uncovered || skip.missing)) || [])
    if (missing.length) return `${copy}: ${missing.join(', ')}`
  }
  return copy
}

function ingressEntry(raw) {
  const e = raw && typeof raw === 'object' ? raw : {}
  return {
    ingress: lower(e.name || e.ingress) || null,
    secret: str(e.secret) || null,
    from: str(e.from) || null,
    to: str(e.to) || null,
    hosts: nameList(e.hosts),
    reason: str(e.reason) || null
  }
}

/**
 * Normalise the bind route's payload into what the tab reports, verbatim and
 * without rounding anything up to "done":
 *
 *   { ok, action, reached, bound[], kept[], skipped[], errors[],
 *     instanceValues[], notPersisted[], line }
 *
 * `bound` is the only list that changed anything. `kept` is an A-26 owner
 * certificate the platform refused to overwrite; `skipped` is everything it
 * looked at and deliberately left; `errors` are the Ingresses it could not
 * patch. `notPersisted` carries the honest caveat that a redeploy may undo a
 * live bind the platform could not store.
 */
export function normalizeBindResult(data) {
  const d = data && typeof data === 'object' ? data : {}
  const bound = (Array.isArray(d.bound_ingresses || d.boundIngresses) ? (d.bound_ingresses || d.boundIngresses) : [])
    .map(ingressEntry)
  const kept = (Array.isArray(d.kept_owner_certificates || d.keptOwnerCertificates)
    ? (d.kept_owner_certificates || d.keptOwnerCertificates)
    : []).map(ingressEntry)
  const skipped = (Array.isArray(d.skipped) ? d.skipped : []).map((s) => ({
    ...ingressEntry(s),
    reasonLabel: skipReasonLabel(s && s.reason, s)
  }))
  const errors = (Array.isArray(d.errors) ? d.errors : [])
    .map((e) => (typeof e === 'string' ? e : str(e && (e.message || e.error)) || JSON.stringify(e)))
    .filter(Boolean)
  const instanceValues = (Array.isArray(d.instance_values || d.instanceValues)
    ? (d.instance_values || d.instanceValues)
    : []).map((v) => {
    const o = v && typeof v === 'object' ? v : {}
    return {
      instanceId: str(o.instance_id || o.instanceId) || null,
      hostnames: nameList(o.hostnames),
      secretName: str(o.secret_name || o.secretName) || null,
      persisted: o.persisted === true,
      conflict: o.conflict === true,
      reason: str(o.reason) || null
    }
  })
  const notPersisted = instanceValues.filter((v) => !v.persisted && v.reason)

  const parts = []
  if (bound.length) parts.push(`${bound.length} ingress${bound.length === 1 ? '' : 'es'} bound`)
  if (kept.length) parts.push(`${kept.length} left on your own certificate`)
  if (skipped.length) parts.push(`${skipped.length} unchanged`)
  if (errors.length) parts.push(`${errors.length} failed`)
  let line
  if (errors.length && !bound.length) line = 'Nothing was bound.'
  else if (!bound.length && !kept.length && !skipped.length) {
    line = d.reached === false
      ? 'The platform couldn’t reach this environment’s ingresses.'
      : 'No ingress in this namespace serves these hostnames.'
  } else line = parts.join(' · ')

  return {
    ok: d.ok !== false && !errors.length,
    action: str(d.action) || (bound.length ? 'bound' : 'noop'),
    reached: d.reached !== false,
    bound,
    kept,
    skipped,
    errors,
    instanceValues,
    notPersisted,
    line
  }
}

/** The one caveat shown when a live bind could not be stored for the next render. */
export const BIND_NOT_PERSISTED_NOTE =
  'The ingress is bound now, but the stored configuration still names the old secret — a redeploy would undo this.'

// ── chips that carry their own exposure ─────────────────────────────────────
// The request dialogue sends `hostnames: [{name, private}]` to
// `registrations/ensure`, so each chip has to remember which lane it is on.
// The default comes from the platform's own reading of public DNS.

/**
 * What the platform knows about one name: `{ name, private, publicDns,
 * publicIp, publicDnsLabel }`. A name the app-view never mentioned (free text,
 * or no app-view at all) is private with `unknown` public DNS.
 */
export function hostnameAdvice(view, name) {
  const n = lower(name)
  const row = ((view && view.hostnames) || []).find((r) => r.hostname === n) || null
  const publicDns = row ? row.publicDns : 'unknown'
  const publicIp = row ? row.publicIp : null
  return {
    name: n,
    private: row ? row.recommendedPrivate !== false : true,
    publicDns,
    publicIp,
    publicDnsLabel: publicDnsLabel(publicDns, publicIp)
  }
}

/** The suggested certificate as toggle-bearing chips, common name first. */
export function suggestedHostnameRows(view) {
  const suggestion = suggestedRequest(view)
  if (!suggestion) return []
  return suggestion.all.map((n) => hostnameAdvice(view, n))
}

/** Are any of these chips on the private lane? Drives the A-20 note. */
export function anyPrivate(rows) {
  return (Array.isArray(rows) ? rows : []).some((r) => r && r.private !== false)
}

// ── lane advice for names with no app behind them ───────────────────────────
// `hostnameAdvice` answers off the app view, which knows only the hostnames an
// app exposes. The SSL tab on `/my/domains` pre-fills names from the domain and
// from the org's environments, so there is no app view to ask — and a name with
// no advice fell back on A-20's "a bare name is private".
//
// `dev.denvstack.com` is a deliberate public front door. Registered private, it
// had no private A record to read back and it answered on public DNS, so step 3
// refused it as though a private name had leaked. The lane, not the name, was
// wrong. `POST /cluster/certificates/hostname-lanes` is the platform's own
// reading of the same names — it writes nothing, and only a name POSITIVELY
// resolved on public DNS comes back `recommended_private: false`, so `absent`
// and `unknown` still open on Private.

export const HOSTNAME_LANES_PATH = '/cluster/certificates/hostname-lanes'

/**
 * The key a hostname is matched on, both sides of the wire: lower-cased and
 * with the root label's trailing dot removed, exactly as cluster-api's
 * `str(h).strip().lower().rstrip(".")` does. Without the dot rule
 * `dev.denvstack.com.` and `dev.denvstack.com` are two different names here
 * and one of them silently has no reading.
 */
function hostKey(value) {
  return lower(value && typeof value === 'object' ? (value.hostname || value.name) : value)
    .replace(/\.+$/, '')
}

/** The names a lane reading is asked for: keyed, de-duplicated, ordered. */
function hostKeys(names) {
  const out = []
  const seen = new Set()
  ;(Array.isArray(names) ? names : []).forEach((entry) => {
    const name = hostKey(entry)
    if (!name || seen.has(name)) return
    seen.add(name)
    out.push(name)
  })
  return out
}

/** The rows a lane reading comes back as, keyed by hostname. `null` → `{}`. */
export function laneMap(lanes) {
  const rows = Array.isArray(lanes) ? lanes : (lanes && Array.isArray(lanes.hostnames) ? lanes.hostnames : [])
  const out = {}
  rows.forEach((row) => {
    const name = hostKey(row)
    if (name) out[name] = row
  })
  return out
}

/**
 * Advice rows for bare `names`, seeded from a lane reading. A name the reading
 * does not cover — and every name when `lanes` is null, which is what a missing
 * or failed reading looks like — stays private with `unknown` public DNS, i.e.
 * exactly the behaviour these rows replace.
 */
export function laneAdviceRows(names, lanes) {
  const by = laneMap(lanes)
  const out = []
  hostKeys(names).forEach((name) => {
    const row = by[name] || null
    const publicDns = row ? publicDnsOf(row) : 'unknown'
    const publicIp = row ? (str(row.public_ip || row.publicIp) || null) : null
    out.push({
      name,
      private: row ? recommendedPrivateOf(row) !== false : true,
      publicDns,
      publicIp,
      publicDnsLabel: publicDnsLabel(publicDns, publicIp)
    })
  })
  return out
}

/**
 * The caution a chip carries when it sits on Private although the platform can
 * see the name answering publicly. It is the client-side twin of step 3's own
 * refusal, said before the request rather than after it.
 */
export const LANE_PUBLIC_WHILE_PRIVATE_NOTE =
  'This name already answers on public DNS, so a private issuance will be refused. Switch it to Public.'

/** Does this chip need that caution? */
export function lanePublicWhilePrivate(row) {
  return !!(row && row.private !== false && publicDnsOf(row) === 'resolves')
}

/**
 * Build the lane-reading client over an axios-like `http`. `classify()` NEVER
 * throws and never partially answers:
 *
 *   { ok: true,  lanes }                 — the platform's reading
 *   { ok: false, missing: true }         — 404: the route isn't deployed here
 *   { ok: false, error: { message } }    — anything else
 *
 * Both failures mean "no reading", and no reading means every name stays
 * private — so an older backend or a quiet resolver costs the lane seeding, not
 * the request.
 */
export function createHostnameLaneApi(http, { unwrap = null } = {}) {
  if (!http) throw new Error('createHostnameLaneApi: http client is required')
  const take = unwrap || ((response) => {
    const body = response && response.data
    if (body && typeof body === 'object' && 'success' in body) {
      if (body.success === false) {
        const err = new Error(body.message || 'Request failed')
        err.response = response
        throw err
      }
      return body.data
    }
    return body
  })
  return {
    async classify(names) {
      const hostnames = hostKeys(names)
      if (!hostnames.length) return { ok: true, missing: false, lanes: {}, error: null }
      try {
        const response = await http.post(HOSTNAME_LANES_PATH, { hostnames })
        return { ok: true, missing: false, lanes: laneMap(take(response)), error: null }
      } catch (err) {
        if (isNotFound(err)) return { ok: false, missing: true, lanes: null, error: null }
        const body = (err && err.response && err.response.data) || {}
        const message = (typeof body.message === 'string' && body.message) ||
          (err && err.message) || 'Request failed'
        const status = err && err.response ? err.response.status : null
        return { ok: false, missing: false, lanes: null, error: { status, message } }
      }
    }
  }
}

// ── the client ──────────────────────────────────────────────────────────────

function isNotFound(err) {
  return !!(err && err.response && err.response.status === 404)
}

/**
 * Build the app-view client over an axios-like `http`. `load()` NEVER throws:
 *
 *   { ok: true,  view }                  — the app's hostname group
 *   { ok: false, missing: true }         — 404: the endpoint isn't deployed here
 *   { ok: false, error: { message } }    — anything else; the tab says so and
 *                                          shows no hostname states at all
 *                                          (a failed lookup is never "uncovered")
 */
export function createAppViewApi(http, { unwrap = null } = {}) {
  if (!http) throw new Error('createAppViewApi: http client is required')
  const take = unwrap || ((response) => {
    const body = response && response.data
    if (body && typeof body === 'object' && 'success' in body) {
      if (body.success === false) {
        const err = new Error(body.message || 'Request failed')
        err.response = response
        throw err
      }
      return body.data
    }
    return body
  })
  return {
    async load({ cluster, namespace, workload, templateId } = {}) {
      const params = {}
      if (str(cluster)) params.cluster = str(cluster)
      if (str(namespace)) params.namespace = str(namespace)
      if (str(workload)) params.workload = str(workload)
      if (str(templateId)) params.template_id = str(templateId)
      try {
        const response = await http.get(APP_VIEW_PATH, { params })
        return { ok: true, missing: false, view: normalizeAppView(take(response)), error: null }
      } catch (err) {
        if (isNotFound(err)) return { ok: false, missing: true, view: null, error: null }
        const body = (err && err.response && err.response.data) || {}
        const message = (typeof body.message === 'string' && body.message) ||
          (err && err.message) || 'Request failed'
        const status = err && err.response ? err.response.status : null
        return { ok: false, missing: false, view: null, error: { status, message } }
      }
    }
  }
}

export const APP_VIEW_MISSING_NOTE =
  'This environment can’t show the app’s hostnames yet — listing the certificates on file instead.'

/** The honest one-liner when the lookup itself failed. */
export function lookupFailureNote(error) {
  const message = error && error.message ? error.message : 'the request failed'
  return `Couldn’t read this app’s hostnames: ${message}. Certificate states are unknown until this succeeds.`
}
